import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  SessionSummaryScreen,
  type TopicResult,
} from "../../screens/practice/SessionSummaryScreen";
import { ScreenState } from "../../components/ScreenState";
import { useAsync } from "../../lib/useAsync";
import { fetchMe } from "../../api/me";
import { Button } from "../../components/Button";

/** How long to wait for the engine before saying we do not have it yet. */
const READINESS_POLL_ATTEMPTS = 6;
const READINESS_POLL_MS = 1500;

/**
 * The session summary, on real numbers.
 *
 * Everything here was invented — 7 of 10, 34% → 37%, a four-day streak and
 * three fabricated topics. The score comes from the submit that just happened;
 * readiness and the streak come from `/api/me`.
 *
 * Readiness is recomputed off a queue rather than inline, so the instant a
 * student finishes there is nothing new to read yet. Two things follow. The
 * "before" is captured when the session *starts* and travels here, which is
 * the only moment it can be taken honestly. And the screen waits a few seconds
 * for the recompute rather than reading the stale number and calling it the
 * result — and if it still is not there, it says so instead of printing a
 * zero. Closes B-60.
 *
 * The screen renders immediately using navigation state (correct/total/topics)
 * with a "Working out your readiness…" indicator, then updates in-place when
 * /api/me resolves. This avoids the flash of a loading skeleton between the
 * last question and the summary.
 */
export function SummaryRoute() {
  const navigate = useNavigate();
  const {
    subject = "",
    subjectId = "",
    correct,
    total,
    queued = false,
    readinessBefore = null,
    topics: topicsFromState = [],
  } = (useLocation().state ?? {}) as {
    subject?: string;
    subjectId?: string;
    correct?: number;
    total?: number;
    queued?: boolean;
    readinessBefore?: number | null;
    topics?: TopicResult[];
  };

  /*
   * A queued session skips `/api/me` entirely. The student is offline — the
   * request would fail and they would be shown a retry screen right after
   * finishing work, which reads as "your session was lost". It was not.
   */
  const { data, loading, error, reload } = useAsync(
    () => (queued ? Promise.resolve(null) : fetchMe()),
    [queued],
  );

  const forSubject = data?.subjects.find((entry) => entry.subjectId === subjectId);
  const readinessAfter = forSubject?.readiness ?? null;

  /*
   * Wait for the engine, briefly.
   *
   * The outbox drains on a timer, so readiness lands a second or two after the
   * summary opens. We keep polling until the value has moved from readinessBefore
   * (or until we exhaust attempts), so a student who already had a score does
   * not see a stale 6% → 6% because the first fetch returned before the engine
   * recomputed.
   */
  const [attempts, setAttempts] = useState(0);
  const hasUpdated = readinessAfter !== null && readinessAfter !== readinessBefore;
  const settled =
    queued || hasUpdated || (readinessAfter !== null && attempts >= READINESS_POLL_ATTEMPTS);
  const pending = !settled && attempts < READINESS_POLL_ATTEMPTS && (data !== null || loading);

  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  useEffect(() => {
    if (!pending || loading) return;

    const timer = setTimeout(() => {
      setAttempts((count) => count + 1);
      reloadRef.current();
    }, READINESS_POLL_MS);

    return () => clearTimeout(timer);
  }, [pending, loading, attempts]);

  if (queued) {
    return (
      <div className="app-column px-5 pb-6 pt-6">
        <p className="m-0 text-[12px] uppercase tracking-[0.12em] text-text-muted">Session done</p>
        <p className="tabular m-0 pt-1 text-[14px] text-text-muted">
          {correct ?? 0} of {total ?? 0} correct
        </p>

        <section className="mt-5 flex flex-col gap-2 rounded-[var(--radius-card)] border border-border bg-surface p-5">
          <p className="m-0 text-[15px] font-semibold">Saved on your phone</p>
          <p className="m-0 text-[13px] text-text-muted">
            You're offline, so this hasn't been marked yet. It sends by itself the moment you're
            back online, and your readiness moves then.
          </p>
        </section>

        <div className="pt-6">
          <Button onClick={() => void navigate("/home")}>Back to home</Button>
        </div>
      </div>
    );
  }

  /*
   * If /api/me has failed and we have no data at all, fall back to the error
   * screen so the student can retry. But only when the initial fetch has
   * actually errored — during loading, we render the summary screen
   * immediately with readinessPending=true.
   */
  if (error && !data) {
    return (
      <ScreenState loading={false} error={error} onRetry={reload}>
        {null}
      </ScreenState>
    );
  }

  const weakest = forSubject?.weakTopics[0];

  return (
    <SessionSummaryScreen
      subject={subject || (forSubject?.name ?? "Practice")}
      correct={correct ?? 0}
      total={total ?? 0}
      readinessBefore={readinessBefore}
      readinessAfter={readinessAfter}
      readinessPending={pending}
      confidence={forSubject?.confidence ?? 0}
      streakDays={data?.student.streakDays ?? 0}
      {...(weakest ? { weakestTopic: weakest } : {})}
      topics={topicsFromState}
      onPractiseWeakest={() => void navigate("/practice/topics", { state: { subject, subjectId } })}
      onHome={() => void navigate("/home")}
    />
  );
}
